import React from "react";
import { TabsContent } from "../ui/tabs";
import { RefreshCw, TrendingUp, ExternalLink, Flame, Sparkles } from "lucide-react";
import ConsentSourcesWidget from "./ConsentSourcesWidget";

export default function AnalyticsTab({ dm, analytics, loadingAnalytics, seedTopPerformersForBrief, seedingTopPerformers }) {
  return (
    <TabsContent value="analytics">
      {loadingAnalytics ? (
        <div className={`text-center py-12 category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Loading analytics…</div>
      ) : !analytics ? (
        <div className={`text-center py-12 category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Click Analytics tab to load data.</div>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total Articles", value: analytics.total_articles?.toLocaleString() },
              { label: "Confirmed Users", value: analytics.total_users?.toLocaleString() },
              { label: "Sources (last 30d)", value: analytics.sources?.length },
              { label: "Days with coverage", value: analytics.daily?.length },
            ].map(({ label, value }) => (
              <div key={label} className={`rounded-xl border p-4 ${dm ? "bg-[#1a2122] border-[#2a3435]" : "bg-white border-[#e8ecee]"}`}>
                <p className={`category-tag text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{label}</p>
                <p className={`editorial-heading text-2xl font-semibold mt-1 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{value ?? "—"}</p>
              </div>
            ))}
          </div>

          {/* Top performing articles this week */}
          <div
            data-testid="top-performing-card"
            className={`rounded-xl border p-6 ${dm ? "bg-[#1a2122] border-[#2a3435]" : "bg-white border-[#e8ecee]"}`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-orange-400/10 text-orange-400" : "bg-orange-500/10 text-orange-600"}`}>
                <Flame className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`editorial-heading text-base font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                  Top performing articles — last 7 days
                </h3>
                <p className={`category-tag text-[11px] mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                  Ranked by signal score (ticker picks +5 · brief inclusions +8 · public clicks +1)
                </p>
              </div>
              {seedTopPerformersForBrief && (
                <button
                  onClick={() => seedTopPerformersForBrief(3)}
                  disabled={seedingTopPerformers || !(analytics.top_performing?.length > 0)}
                  data-testid="seed-brief-top-performers-btn"
                  title="Use the top 3 for the next brief — also applies to next auto-newsletter & podcast"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full category-tag transition-all disabled:opacity-50 flex-shrink-0 ${
                    dm ? "bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"
                  }`}
                >
                  {seedingTopPerformers
                    ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Seeding…</>
                    : <><Sparkles className="w-3.5 h-3.5" />Use top 3 for next brief</>
                  }
                </button>
              )}
            </div>

            {/* Top topics chip row */}
            {analytics.top_topics?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-dashed"
                   style={{ borderColor: dm ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }}>
                <span className={`category-tag text-[11px] ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Hot topics:</span>
                {analytics.top_topics.map((t) => (
                  <span
                    key={t.category}
                    data-testid={`top-topic-${t.category}`}
                    title={`${t.total_signal} signal · ${t.total_clicks} clicks · ${t.article_count} articles`}
                    className={`category-tag px-2.5 py-0.5 rounded-full text-[11px] flex items-center gap-1 ${
                      dm ? "bg-cyan-400/10 text-cyan-300" : "bg-[#006874]/8 text-[#006874]"
                    }`}
                  >
                    {t.category}
                    <strong className="font-semibold">{t.total_signal}</strong>
                  </span>
                ))}
              </div>
            )}

            {analytics.top_performing?.length > 0 ? (
              <div className="space-y-2">
                {analytics.top_performing.map((a, i) => (
                  <a
                    key={`${a.originalUrl || a.title}-${i}`}
                    href={a.originalUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`top-perf-row-${i}`}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors group ${
                      dm ? "bg-white/3 hover:bg-white/5" : "bg-[#f8f9fa] hover:bg-[#eef0f2]"
                    }`}
                  >
                    {/* Rank */}
                    <span
                      className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0
                          ? "bg-orange-500/15 text-orange-500"
                          : i === 1
                          ? "bg-amber-400/15 text-amber-500"
                          : i === 2
                          ? "bg-yellow-400/15 text-yellow-600"
                          : dm
                          ? "bg-white/5 text-slate-500"
                          : "bg-[#e8ecee] text-[#6b7a7d]"
                      }`}
                    >
                      {i + 1}
                    </span>

                    {/* Title + source */}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate group-hover:text-[#006874] transition-colors ${
                          dm ? "text-slate-200 group-hover:text-cyan-300" : "text-[#191c1d]"
                        }`}
                      >
                        {a.title}
                      </p>
                      <p className={`category-tag text-[10px] truncate ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                        {a.source} · {a.category}
                      </p>
                    </div>

                    {/* Score */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full category-tag text-[11px] ${
                          dm ? "bg-cyan-400/10 text-cyan-300" : "bg-[#006874]/8 text-[#006874]"
                        }`}
                        title={`signal ${a.signal_score} · clicks ${a.public_clicks}`}
                      >
                        <TrendingUp className="w-2.5 h-2.5" />
                        {a.signal_score}
                        {a.public_clicks > 0 && (
                          <span className={dm ? "text-cyan-500/70" : "text-[#006874]/60"}>·{a.public_clicks}</span>
                        )}
                      </span>
                      <ExternalLink className={`w-3.5 h-3.5 flex-shrink-0 ${dm ? "text-slate-600 group-hover:text-cyan-400" : "text-[#bac9cc] group-hover:text-[#006874]"}`} />
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className={`text-center py-6 category-tag text-sm ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                No traction yet this week. Articles need ticker picks, brief inclusions, or public clicks to score.
              </div>
            )}
          </div>

          <div className={`rounded-xl border p-6 ${dm ? "bg-[#1a2122] border-[#2a3435]" : "bg-white border-[#e8ecee]"}`}>
            <h3 className={`editorial-heading text-base font-medium mb-4 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Articles Published — Last 30 Days</h3>
            {analytics.daily?.length > 0 ? (() => {
              const max = Math.max(...analytics.daily.map(d => d.count), 1);
              return (
                <div className="flex items-end gap-1 h-24 overflow-x-auto">
                  {analytics.daily.map(d => (
                    <div key={d.date} className="flex flex-col items-center gap-1 flex-1 min-w-[18px]" title={`${d.date}: ${d.count} articles`}>
                      <div
                        className="w-full rounded-sm bg-[#006874] opacity-80 hover:opacity-100 transition-all"
                        style={{ height: `${Math.max(4, (d.count / max) * 80)}px` }}
                      />
                      <span className={`category-tag text-[9px] rotate-45 origin-left whitespace-nowrap ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{d.date.slice(5)}</span>
                    </div>
                  ))}
                </div>
              );
            })() : <p className={`category-tag text-sm ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>No daily data yet.</p>}
          </div>

          <div className={`rounded-xl border p-6 ${dm ? "bg-[#1a2122] border-[#2a3435]" : "bg-white border-[#e8ecee]"}`}>
            <h3 className={`editorial-heading text-base font-medium mb-4 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Top Sources by Article Count</h3>
            <div className="space-y-2">
              {(() => {
                const max = Math.max(...(analytics.sources || []).map(s => s.count), 1);
                return (analytics.sources || []).map(s => (
                  <div key={s.source} className="flex items-center gap-3">
                    <span className={`category-tag text-xs w-36 truncate flex-shrink-0 ${dm ? "text-slate-400" : "text-[#191c1d]"}`}>{s.source}</span>
                    <div className="flex-1 bg-[#e8ecee] dark:bg-[#2a3435] rounded-full h-2 overflow-hidden">
                      <div className="h-full rounded-full bg-[#006874]" style={{ width: `${(s.count / max) * 100}%` }} />
                    </div>
                    <span className={`category-tag text-xs w-8 text-right flex-shrink-0 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{s.count}</span>
                  </div>
                ));
              })()}
            </div>
          </div>

          {analytics.categories?.length > 0 && (
            <div className={`rounded-xl border p-6 ${dm ? "bg-[#1a2122] border-[#2a3435]" : "bg-white border-[#e8ecee]"}`}>
              <h3 className={`editorial-heading text-base font-medium mb-4 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Article Categories</h3>
              <div className="flex flex-wrap gap-2">
                {analytics.categories.map(c => (
                  <span key={c.category} className={`category-tag px-3 py-1 rounded-full text-xs border ${dm ? "border-[#2a3435] text-slate-400" : "border-[#e8ecee] text-[#6b7a7d]"}`}>
                    {c.category} <strong>{c.count}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Subscriber acquisition by source — self-fetching widget */}
          <ConsentSourcesWidget dm={dm} />
        </div>
      )}
    </TabsContent>
  );
}
